import { useEffect, useMemo, useState } from 'react';

// components
import DialogWithConfirmActions from '@/pwa/atoms/DialogWithConfirmActions/DialogWithConfirmActions';

import OrganiserEntryForm, {
  EntryDialogCalendar,
  EntryState,
  isFormValid,
} from '../OrganiserEntryForm/OrganiserEntryForm';

interface CreateEntryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Available calendars (care circles) — shown as a picker in create mode. */
  calendars: EntryDialogCalendar[];
  onSave: (entry: EntryState) => void;
  isSaving?: boolean;
}

const initialCreateEntryState = () => {
  return {
    calendarId: '',
    title: '',
    assigneeAccountId: null,
    startTime: null,
    endTime: null,
    isRecurring: false,
    recurrenceFrequency: 'weekly',
    recurrenceInterval: 1,
    isRepeatingTaskEntry: false,
    isUpdatingRepeatingTask: false,
    recurringEventId: null,
    occurrenceDate: null,
  } as EntryState;
};

/**
 * CreateEntryDialog is a MUI Dialog for creating an organiser entry.
 */
export const CreateEntryDialog = ({
  isOpen,
  onClose,
  calendars,
  onSave,
  isSaving = false,
}: CreateEntryDialogProps) => {
  const [draftEntry, setDraftEntry] = useState<EntryState>(
    initialCreateEntryState(),
  );

  useEffect(() => {
    if (isOpen && calendars.length > 0 && !draftEntry.calendarId) {
      setDraftEntry((s) => ({ ...s, calendarId: calendars[0].calendarId }));
    }
  }, [isOpen, calendars, draftEntry]);

  const isValid = useMemo(() => isFormValid(draftEntry), [draftEntry]);

  return (
    <DialogWithConfirmActions
      title='Add action'
      isOpen={isOpen}
      onClose={onClose}
      actions={[
        {
          variant: 'contained',
          size: 'large',
          label: isSaving ? 'Saving...' : 'Save',
          onAction: () => onSave(draftEntry),
          isDisabled: isSaving || !isValid,
        },
      ]}
    >
      <OrganiserEntryForm
        draftEntry={draftEntry}
        setDraftEntry={setDraftEntry}
        mode='create'
        calendars={calendars}
      />
    </DialogWithConfirmActions>
  );
};

CreateEntryDialog.displayName = 'CreateEntryDialog';
export default CreateEntryDialog;
